import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useToaster } from '@components/toaster';

import { unhandledRequest } from 'api/request';
import { useStore } from 'models/Provider';

export type SignetToken = {
  username: string;
  password: string;
  certificatePassword: string;
  certificate: File | null;
};

export type SignetTokenError = AxiosError<{
  ok: boolean;
  message: string;
}>;

export type Cosigner = {
  id: number;
  name: string;
  email: string;
  ssn: string;
};

export type EditCosigner = Omit<Cosigner, 'id'>;

export type CosignerError = AxiosError<{
  nonFieldErrors: Array<String>;
}>;

export async function signetGetToken() {
  const response = await unhandledRequest.get<{ ok: boolean; valid: boolean }>({
    url: `/integrations/signet/settings/token/`,
  });
  return response.data;
}

function parseData(input: SignetToken): FormData {
  const data = new FormData();
  data.append('username', input.username || '');
  data.append('password', input.password || '');
  data.append('certificate_password', input.certificatePassword || '');
  data.append('certificate', input.certificate || '');
  return data;
}

export async function signetSetToken(input: SignetToken) {
  const response = await unhandledRequest.post<{ ok: boolean }>({
    url: `/integrations/signet/settings/token/`,
    data: parseData(input),
  });
  return response.data;
}

export async function signetEditToken(input: SignetToken) {
  const response = await unhandledRequest.patch<{ ok: boolean }>({
    url: `/integrations/signet/settings/token/`,
    data: parseData(input),
  });
  return response.data;
}

export async function signetGetCosigners() {
  const response = await unhandledRequest.get<Cosigner[]>({
    url: `/integrations/signet/settings/cosigners/`,
  });
  return response.data;
}

export async function signetAddCosigner(data: EditCosigner) {
  const response = await unhandledRequest.post({
    url: `/integrations/signet/settings/cosigners/`,
    data,
  });
  return response.data;
}

export async function signetEditCosigner(cosignerId: string, data: EditCosigner) {
  const response = await unhandledRequest.patch({
    url: `/integrations/signet/settings/cosigners/${cosignerId}/`,
    data,
  });
  return response.data;
}

export async function signetRemoveCosigner(cosignerId: string) {
  const response = await unhandledRequest.delete({
    url: `/integrations/signet/settings/cosigners/${cosignerId}/`,
  });
  return response.data;
}

export const useConnectSignet = (
  isEditing: boolean,
  options?: UseMutationOptions<unknown, unknown, SignetToken>,
) => {
  const { t } = useStore();
  const { showSuccess, showError } = useToaster();
  const client = useQueryClient();

  return useMutation<unknown, SignetTokenError, SignetToken>({
    ...options,
    mutationFn: isEditing ? signetEditToken : signetSetToken,
    onSuccess: (...data) => {
      showSuccess(t('integrations.signet.settings.connect.modal.saved'));
      client.resetQueries(['signet-token']);
      options?.onSuccess?.(...data);
    },
    onError: (data, ...rest) => {
      if (data.response?.data.message) {
        showError(t(data.response.data.message));
      } else {
        showError(t('integrations.signet.settings.connect.modal.error'));
      }
      options?.onError?.(data, ...rest);
    },
  });
};

export const useManageSignetCosigner = (
  id?: number,
  options?: UseMutationOptions<unknown, CosignerError, EditCosigner>,
) => {
  const { t } = useStore();
  const { showSuccess, showError } = useToaster();
  const client = useQueryClient();
  const isEditing = id !== undefined;

  return useMutation({
    mutationFn: (data) => {
      return isEditing ? signetEditCosigner(String(id), data) : signetAddCosigner(data);
    },
    ...options,
    onSuccess: (...data) => {
      const message = isEditing
        ? t('integrations.signet.settings.cosigners.edit.success')
        : t('integrations.signet.settings.cosigners.add.success');
      showSuccess(message);
      client.resetQueries(['signet-cosigners']);
      options?.onSuccess?.(...data);
    },
    onError: (data, ...rest) => {
      if (data.response?.data.nonFieldErrors) {
        showError(data.response.data.nonFieldErrors.join(' '));
      } else {
        showError(t('integrations.signet.settings.cosigners.modal.error'));
      }
      options?.onError?.(data, ...rest);
    },
  });
};
